import { ObservableVec3 } from '../math/observable-vec3.js';
import { ObservableQuat } from '../math/observable-quat.js';
import { createWorldMatrixState, attachWorldMatrixState, composeTrsLocalMatrix } from './world-matrix-state.js';
import { eulerToQuat, quatToEulerXYZ } from '../math/quat-euler.js';

function createEulerProxy(rq) {
  let ex = 0;
  let ey = 0;
  let ez = 0;
  let syncedVersion = -1;
  const sync = () => {
    if (rq.version !== syncedVersion) {
      const e = quatToEulerXYZ(rq.x, rq.y, rq.z, rq.w);
      ex = e[0];
      ey = e[1];
      ez = e[2];
      syncedVersion = rq.version;
    }
  };
  const apply = (x, y, z) => {
    ex = x;
    ey = y;
    ez = z;
    const [a, b, c, d] = eulerToQuat(x, y, z);
    rq.set(a, b, c, d);
    syncedVersion = rq.version;
  };
  return {
    get x() {
      sync();
      return ex;
    },
    set x(v) {
      sync();
      apply(v, ey, ez);
    },
    get y() {
      sync();
      return ey;
    },
    set y(v) {
      sync();
      apply(ex, v, ez);
    },
    get z() {
      sync();
      return ez;
    },
    set z(v) {
      sync();
      apply(ex, ey, v);
    },
    set: apply
  };
}
function createSceneNode(name, px = 0, py = 0, pz = 0, qx = 0, qy = 0, qz = 0, qw = 1, sx = 1, sy = 1, sz = 1) {
  return createSceneNodeCore(name, null, px, py, pz, qx, qy, qz, qw, sx, sy, sz);
}
function createSceneNodeFromMatrix(name, matrix) {
  return createSceneNodeCore(name, matrix);
}
function createSceneNodeCore(name, matrix, px = 0, py = 0, pz = 0, qx = 0, qy = 0, qz = 0, qw = 1, sx = 1, sy = 1, sz = 1) {
  const wm = createWorldMatrixState(() => {
    return node._localMatrix ?? composeTrsLocalMatrix(node.position, node.rotationQuaternion, node.scaling);
  });
  const onWmDirty = () => {
    if (!node._localMatrix) {
      wm.markLocalDirty();
    }
  };
  const position = new ObservableVec3(px, py, pz, onWmDirty);
  const rq = new ObservableQuat(qx, qy, qz, qw, onWmDirty);
  const rotation = createEulerProxy(rq);
  const scaling = new ObservableVec3(sx, sy, sz, onWmDirty);
  const node = {
    name,
    children: [],
    position,
    rotationQuaternion: rq,
    rotation,
    scaling,
    get parent() {
      return wm.parent;
    },
    set parent(v) {
      wm.parent = v;
    },
    get worldMatrix() {
      return wm.getWorldMatrix();
    },
    get worldMatrixVersion() {
      return wm.getWorldMatrixVersion();
    }
  };
  if (matrix) {
    node._localMatrix = matrix;
  }
  attachWorldMatrixState(node, wm);
  return node;
}

export { createEulerProxy, createSceneNode, createSceneNodeFromMatrix };
//# sourceMappingURL=scene-node.js.map
